import {
  useClientDashboardQuery,
  useClientUpdatedSubscription,
  useClientVettersOnlineQuery,
} from '@vette/data-access';
import {
  exportDetailedData,
  exportPerformanceData,
  hooks,
} from '@vette/frontend-utils';
import { Space } from 'antd';
import React, { useMemo, useState } from 'react';
import { Dropdown } from 'antd';
import { Menu } from 'antd';
import { Select } from 'antd';
import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subMonths,
} from 'date-fns';
import { DashboardCard } from './DashboardCard';
import * as S from './styles';

type DashboardPeriod = 'ALL' | 'MONTH' | 'QUARTER' | 'LAST_MONTH';

// Computes the createdAt window for the dashboard counts. ALL => no bounds
// (all-time, the prior behavior). Month-over-month is a follow-on: issue the
// query a second time with the previous window and show the delta.
const periodToRange = (
  period: DashboardPeriod
): { from?: string; to?: string } => {
  const now = new Date();
  switch (period) {
    case 'MONTH':
      return {
        from: startOfMonth(now).toISOString(),
        to: endOfMonth(now).toISOString(),
      };
    case 'QUARTER':
      return {
        from: startOfQuarter(now).toISOString(),
        to: endOfQuarter(now).toISOString(),
      };
    case 'LAST_MONTH': {
      const lastMonth = subMonths(now, 1);
      return {
        from: startOfMonth(lastMonth).toISOString(),
        to: endOfMonth(lastMonth).toISOString(),
      };
    }
    case 'ALL':
    default:
      return {};
  }
};

type Props = {
  clientId: string;
  userId: string;
  canManageBilling: boolean;
};

export const Dashboard: React.FC<Props> = ({ clientId, userId }) => {
  // Default to last month, not all-time: a cumulative all-time number reads as
  // meaningless to clients. Last month (not "this month") because a partial or
  // not-yet-started current month can show near-zero. All-time stays selectable.
  const [period, setPeriod] = useState<DashboardPeriod>('LAST_MONTH');
  const { from, to } = useMemo(() => periodToRange(period), [period]);

  const { data, loading, refetch } = useClientDashboardQuery({
    variables: { clientId, userId, from, to },
  });

  // Per-client flag, OFF by default. When off, the vetter card is fully absent
  // AND we do not fetch the count (skip the query entirely).
  const showVetterAvailability =
    data?.client?.__typename === 'Client'
      ? data.client.showVetterAvailability ?? false
      : false;

  const { data: vetterData, loading: vettersLoading } =
    useClientVettersOnlineQuery({
      variables: { clientId },
      pollInterval: 30000,
      skip: !showVetterAvailability,
    });
  const vettersOnline = vetterData?.clientVettersOnline.online ?? 0;

  hooks.useThrottleSubscription(refetch, 2000, useClientUpdatedSubscription, {
    variables: {
      clientId,
    },
  });

  const { engaged = 0, passed = 0, vetted = 0 } = data?.clientDashboard || {};

  const cardData = useMemo(
    () => [
      {
        label: 'Engaged',
        value: engaged,
        src: '/images/engaged-photo.svg',
        opacity: 0.6,
        grayscale: engaged === 0 || engaged === null,
        tooltip:
          'Applicants who entered SMS outreach for one of your jobs (excludes automated re-engagement/follow-up retries). Counted by when the applicant was added. Note: these four cards are independent counts anchored on different dates — not a strict funnel, so they will not add up cleanly within one window.',
      },
      {
        label: 'Interviewed',
        value: vetted,
        src: '/images/interviewed-photo.svg',
        opacity: 0.6,
        grayscale: vetted === 0 || vetted === null,
        tooltip:
          'Applicants whose screening reached a decision — they finished the interview or failed a knockout question. Counted by when the screening happened (a different date basis than Engaged, so the cards do not stack as a funnel).',
      },
      {
        label: 'Passed',
        value: passed,
        src: '/images/passed-photo.svg',
        grayscale: passed === 0 || passed === null,
        opacity: 1,
        tooltip:
          'Applicants the screening recommended — they passed. Counted by when the screening happened. Independent count, not a strict funnel with the other cards.',
      },
    ],
    [engaged, passed, vetted]
  );
  type ExportKey = 'SESSION-DATA' | 'AGGREGATED-DATA';

  const newEnum: { [K in ExportKey]: K } = {
    'SESSION-DATA': 'SESSION-DATA',
    'AGGREGATED-DATA': 'AGGREGATED-DATA',
  };

  const exportData = (key: ExportKey) => {
    // Export the SAME window shown on-screen so the CSV reconciles with the
    // dashboard cards (the export path applies the same channel + date filter).
    if (key === newEnum['SESSION-DATA']) {
      exportDetailedData(clientId, { from, to });
    } else if (key === newEnum['AGGREGATED-DATA']) {
      exportPerformanceData(clientId, { from, to });
    }
  };

  const menu = (
    <Menu
      onClick={e => exportData(e.key as ExportKey)}
      items={[
        {
          label: 'Export session data',
          key: 'SESSION-DATA',
        },
        {
          label: 'Export aggregated data',
          key: 'AGGREGATED-DATA',
        },
      ]}
    />
  );

  return (
    <>
      <S.Header className="dashboard-manager">
        <S.Heading>
          {data?.user?.firstName ? `Hi ${data?.user?.firstName}` : 'Dashboard'}
        </S.Heading>
        <Space size={12}>
          {/* Explicit window label so a client landing on the new Last-month
              default immediately sees which window they're viewing, not just
              the picker control. */}
          <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            Showing:
          </span>
          <Select<DashboardPeriod>
            value={period}
            onChange={setPeriod}
            size="large"
            style={{ minWidth: 160 }}
            options={[
              { label: 'All time', value: 'ALL' },
              { label: 'This month', value: 'MONTH' },
              { label: 'This quarter', value: 'QUARTER' },
              { label: 'Last month', value: 'LAST_MONTH' },
            ]}
          />
          <Dropdown overlay={menu} trigger={['click']}>
            <S.Button type="primary">
              Export data
              {<S.DropdownOutlined />}
            </S.Button>
          </Dropdown>
        </Space>
      </S.Header>
      <S.Cards>
        {showVetterAvailability && (
          <DashboardCard
            key="Vetters Online"
            loading={vettersLoading}
            label="Vetters Online"
            src="/images/engaged-photo.svg"
            backgroundSize="contain"
            tooltip="How many of your vetters are online right now — a live capacity gauge before launching a large batch. Excludes external/shared vetters."
            grayscale={!vettersLoading && vettersOnline === 0}
            opacity={vettersOnline === 0 ? 0.6 : undefined}
          >
            <S.PrimaryText>{vettersOnline}</S.PrimaryText>
          </DashboardCard>
        )}
        {cardData.map(({ label, value, src, grayscale, opacity, tooltip }) => (
          <DashboardCard
            key={label}
            loading={loading}
            label={label}
            src={src ?? ''}
            backgroundSize="contain"
            tooltip={tooltip}
            grayscale={!loading && grayscale}
            opacity={grayscale ? opacity : undefined}
          >
            <S.PrimaryText>{value}</S.PrimaryText>
          </DashboardCard>
        ))}
      </S.Cards>
    </>
  );
};
